/* eslint-disable */
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import './lostDroppedReport.scss';
import '../WeeklyClosureReport/clouserReport.scss';
import DataGridComponent from '../../../../Components/DataGrid/dataGrid';
import {
  LOST_DROPPED_REPORT,
  DATE_NOTE_HEADING,
  DATE_NOTE_DESCRIPTION,
} from '../../../../Constants/strings';
import { useLocation } from 'react-router-dom';
import ButtonComponent from '../../../../Components/Button/button';
import { Menu, MenuItem } from '@mui/material';
import ArrowDropDown from '../../../../Assets/images/svg/drop-down-arrow.svg';
import excelIcon from '../../../../Assets/images/svg/excel.svg';
import excelhoverIcon from '../../../../Assets/images/svg/excel_hover.svg';
import { useSnackbar } from '../../../../Components/Snackbar/snackBarProvider';
import DateRangeEditor from '../../../../Components/CustomDateRange/dateRangeInput';
import { getstaffLostDropReportExcel } from '../../../../Api/request/reports';
import { defaultDateRanges } from '../defaultDate';
import { format } from 'date-fns';

const LostDroppedReport = ({
  rows,
  lostDroppedData,
  pageSize,
  dateRange,
  setDateRange,
  onDateChange,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isExcelHovered, setIsExcelHovered] = useState(false);
  const { snackbarOpen } = useSnackbar();
  const location = useLocation();

  const [viewport, setViewport] = useState({
    isMobile: false,
    isLaptop: false,
  });

  // ---------- UTILS ----------
  const formatDate = (date) => (date ? format(date, 'yyyy-MM-dd') : '');

  const handleDateRangeChange = (newDateRange) => {
    const { startDate, endDate } = newDateRange;

    const parsedStartDate = startDate ? new Date(startDate) : null;
    const parsedEndDate = endDate ? new Date(endDate) : null;

    if (parsedStartDate && isNaN(parsedStartDate)) {
      console.error('Invalid startDate in handleDateRangeChange:', startDate);
      return;
    }

    if (parsedEndDate && isNaN(parsedEndDate)) {
      console.error('Invalid endDate in handleDateRangeChange:', endDate);
      return;
    }

    setDateRange({ startDate: parsedStartDate, endDate: parsedEndDate });

    if (parsedStartDate && parsedEndDate) {
      onDateChange(parsedStartDate, parsedEndDate);
    }
  };

  const handleDownloadClick = (event) => setAnchorEl(event.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const handleDownloadExcel = async () => {
    try {
      const startDate = formatDate(dateRange.startDate);
      const endDate = formatDate(dateRange.endDate);
      await getstaffLostDropReportExcel(startDate, endDate);
    } catch (error) {
      snackbarOpen(
        LOST_DROPPED_REPORT.DOWNLOAD_FAILURE_MESSAGE,
        LOST_DROPPED_REPORT.ERROR_SEVERITY
      );
    } finally {
      handleCloseMenu();
    }
  };

  const getUpdatedRowsWithTotal = useMemo(() => {
    if (!rows?.length) return [];
    return [
      ...rows,
      {
        id: 'Total',
        reason: 'Total',
        droppedPositions: lostDroppedData?.totalDroppedPositions || 0,
        lostPositions: lostDroppedData?.totalLostPositions || 0,
      },
    ];
  }, [rows, lostDroppedData]);

  const updateViewport = () => {
    const width = window.innerWidth;
    setViewport({
      isMobile: width < 1024,
      isLaptop: width >= 1024 && width <= 1200,
    });
  };

  // ---------- EFFECTS ----------
  useEffect(() => {
    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, [location]);

  useEffect(() => {
    if (dateRange?.startDate && dateRange?.endDate) {
      onDateChange(dateRange.startDate, dateRange.endDate);
    }
  }, [dateRange]);

  const getColumnWidth = (mobile, laptop, desktop) =>
    viewport.isMobile ? mobile : viewport.isLaptop ? laptop : desktop;

  const columns = useMemo(() => {
    return [
      {
        field: LOST_DROPPED_REPORT.LOST_DROP_MASTER_FIELD.REASON,
        headerName: LOST_DROPPED_REPORT.LOST_DROP_MASTER_HEADERNAME.REASON,
        width: getColumnWidth(280, 320, 350),
        minWidth: 220,
        flex: 0.35,
        sortable: false,
        disableColumnMenu: true,
        hideCoumnMenuIcon: true,
        cellClassName: 'lost-dropped-cell',
      },
      {
        field: LOST_DROPPED_REPORT.LOST_DROP_MASTER_FIELD.DEAL_TYPE,
        headerName: LOST_DROPPED_REPORT.LOST_DROP_MASTER_HEADERNAME.DEAL_TYPE,
        width: getColumnWidth(180, 220, 150),
        minWidth: 180,
        flex: 0.15,
        sortable: false,
        disableColumnMenu: true,
        hideCoumnMenuIcon: true,
        cellClassName: 'lost-dropped-cell',
      },
      {
        field: LOST_DROPPED_REPORT.LOST_DROP_MASTER_FIELD.GEOGRAPHY,
        headerName: LOST_DROPPED_REPORT.LOST_DROP_MASTER_HEADERNAME.GEOGRAPHY,
        width: getColumnWidth(180, 220, 150),
        minWidth: 180,
        flex: 0.15,
        sortable: false,
        disableColumnMenu: true,
        hideCoumnMenuIcon: true,
        cellClassName: 'lost-dropped-cell',
      },
      {
        field: LOST_DROPPED_REPORT.LOST_DROP_MASTER_FIELD.DROPPED,
        headerName: LOST_DROPPED_REPORT.LOST_DROP_MASTER_HEADERNAME.DROPPED,
        width: getColumnWidth(150, 150, 200),
        minWidth: 150,
        flex: 0.13,
        sortable: false,
        disableColumnMenu: true,
        hideCoumnMenuIcon: true,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: LOST_DROPPED_REPORT.LOST_DROP_MASTER_FIELD.LOST,
        headerName: LOST_DROPPED_REPORT.LOST_DROP_MASTER_HEADERNAME.LOST,
        width: getColumnWidth(150, 150, 200),
        minWidth: 110,
        flex: 0.1,
        sortable: false,
        disableColumnMenu: true,
        hideCoumnMenuIcon: true,
        align: 'center',
        headerAlign: 'center',
      },
    ];
  }, [viewport]);

  const sortFilterColumns = useMemo(
    () => [
      LOST_DROPPED_REPORT.LOST_DROP_MASTER_FIELD.REASON,
      LOST_DROPPED_REPORT.LOST_DROP_MASTER_FIELD.DEAL_TYPE,
      LOST_DROPPED_REPORT.LOST_DROP_MASTER_FIELD.GEOGRAPHY,
    ],
    []
  );

  // ---------- RENDER ----------
  return (
    <div className="lost-drop-report-outer-layer">
      <div className="lost-drop-report">
        <div className="lost-drop-top-section">
          <div className="lost-drop-date-filter">
            <DateRangeEditor
              key={`lost-dropped-date-range-${dateRange.startDate}-${dateRange.endDate}`}
              dateRange={{
                startDate: dateRange.startDate ? new Date(dateRange.startDate) : null,
                endDate: dateRange.endDate ? new Date(dateRange.endDate) : null,
              }}
              onChange={(newDateRange) => handleDateRangeChange(newDateRange)}
              fromPlaceholder="From Date"
              toPlaceholder="To Date"
              definedRanges={defaultDateRanges}
              dateFormat="dd/mm/yyyy"
              useCurrentYear
              splitInitialCustomMonthView={true}
            />
            <div className="staff-aug-date-range-note">
              <strong className="staff-aug-date-heading">
                {DATE_NOTE_HEADING}
              </strong>{' '}
              {DATE_NOTE_DESCRIPTION}
            </div>
          </div>
          <div>
            <ButtonComponent
              label={LOST_DROPPED_REPORT.DOWNLOAD}
              variant="text"
              btnCss="download-skill-btn"
              onClick={handleDownloadClick}
              endIcon={<img src={ArrowDropDown} alt="Arrow Dropdown" />}
            />
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
              <MenuItem
                className="downloadskill-dropdown"
                onClick={handleDownloadExcel}
                onMouseEnter={() => setIsExcelHovered(true)}
                onMouseLeave={() => setIsExcelHovered(false)}
              >
                <img
                  src={isExcelHovered ? excelhoverIcon : excelIcon}
                  alt="Excel Icon"
                  style={{ marginRight: 8 }}
                />
                Download as Excel
              </MenuItem>
            </Menu>
          </div>
        </div>

        <div className="lost-drop-report-grid-container">
          <DataGridComponent
            dataRows={getUpdatedRowsWithTotal}
            dataColumns={columns}
            sortFilterColumns={sortFilterColumns}
            pageSize={pageSize}
            hideFooter
            hideSeparator
            isFilteredRows={getUpdatedRowsWithTotal}
            dataGridCss="lost-drop-report-grid"
            sortingMode="server"
            paginationMode="server"
          />
        </div>
      </div>
    </div>
  );
};

LostDroppedReport.propTypes = {
  rows: PropTypes.array.isRequired,
  lostDroppedData: PropTypes.object,
  pageSize: PropTypes.number.isRequired,
  dateRange: PropTypes.object.isRequired,
  setDateRange: PropTypes.func.isRequired,
  onDateChange: PropTypes.func.isRequired,
};

export default LostDroppedReport;
